import * as THREE from "three";
import { groundAt } from "../terrain";
import { Heading } from "@highmarch/domain";
import { VFX } from "../../../../../art/vfx.js";
import { hqNode } from "./common";
import { SkinFx } from "../skins/kit/fx";
import { TowerModel, aimWeapon, weaponOverlay } from "./towerModel";
import { partBox } from "./towerMuzzle";
import { TowerFeel } from "./towerFeel";
import { RELEASE_FPS, TOWER_SHOT, releaseSeconds } from "./projectiles";
import { reducedVfx } from "../../ui/settings";
import { Brazier, TowerView, WeaponPose } from "./towerView";
import { FortressRhythm } from "./towerRhythm";
import { fortressAwaken, fortressIdle } from "./fortressLife";

/**
 * The towers' body language between shots (towers.ts TowerViews drives it per frame): the landing and shot squash,
 * the weapon's pose and aim, the loaded round, idle arcs and embers, the tier-3 crown, the fortress's charge glow and
 * the Dragonlord reveal, and the fire tower's sprite braziers. Free functions over one tower's view state.
 */

/** The glow-flash hook (TowerViews.glowFlash). */
export type GlowFlash = (root: THREE.Object3D, gain: number, seconds: number) => void;

const UP = new THREE.Vector3(0, 1, 0);
const _orbit = new THREE.Vector3();
const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _m = new THREE.Matrix4();

/** A `fire` clip may restart once it is this far through (towers.md F8: fast towers used to swallow most shots). */
export const FIRE_RESTART_AT = 0.5;

/**
 * A shot's body language: clip (restartable, fitted to the fire interval, over the idle rather than averaged with
 * it), squash, glow flash; kick and dust for the heavy guns.
 */
export function shotFeel(v: TowerView, dir: THREE.Vector3, rate: number, fx: VFX | null): void {
  // the kick goes back along the barrel as it points, which (towerAim.ts) may still be coming round to `dir`
  if (v.aim) dir = _b.set(Math.sin(v.yaw), 0, Math.cos(v.yaw));
  const rig = v.model.rig;
  const firing = v.fireAt < v.fireLen;
  if (!rig.busy || (firing && v.fireAt > v.fireLen * FIRE_RESTART_AT)) {
    const len = rig.playOnce("fire", false, null, { timeScale: rate, solo: true });
    if (len > 0) { v.fireAt = 0; v.fireLen = len / rate; v.fireRate = rate; }
  }
  v.shotAt = 0;
  const heavy = v.arch === "cannon" || v.arch === "mortar";
  const d = Math.hypot(dir.x, dir.z);
  v.kickX = heavy && d > 1e-3 ? -dir.x / d : 0; v.kickZ = heavy && d > 1e-3 ? -dir.z / d : 0;
  // the glow flashes as the round leaves (the clip's release frame), not on the sim's shot a quarter second before
  const shot = TOWER_SHOT[v.arch];
  v.glowIn = shot ? releaseSeconds(shot, rate) : 0;
  if (heavy && !reducedVfx()) fx?.shotDust(_a.copy(v.root.position));
}

/**
 * A fortress shot: the strike when the fortress is charged — or when this is the shot its charge was planned to end
 * on — playing `fire` over the idle (the lanterns keep swinging); a zap otherwise, with no clip (heroShot tells the
 * VFX router which).
 */
export function baseShot(v: TowerView, glowFlash: GlowFlash | null): void {
  v.hero = v.phase === "ready" || (v.phase === "charging" && v.chargeAt >= v.strikeAt - FortressRhythm.StrikeSlack);
  if (!v.hero) return;
  v.phase = "spent";
  v.chargeAt = Infinity;
  const len = v.model.rig.playOnce("fire");
  if (len > 0) { v.fireAt = 0; v.fireLen = len; v.fireRate = 1; }
  glowFlash?.(v.model.storeys, TowerFeel.GlowGain, TowerFeel.GlowSeconds);
}

/**
 * A placed tower's landing (owner report 2026-09-23: placing wants a "thump"): height 1 → 0.84 on impact, a
 * 1.1 stretch on the rebound, a small settle, volume kept (width the inverse square root). The shot squash
 * (TowerFeel.squash, 0.96) was too slight to read as weight arriving.
 */
export const LAND_KEYS: readonly (readonly [number, number])[] = [[0, 1], [0.05, 0.84], [0.13, 1.1], [0.22, 0.97], [0.3, 1]];
export const LAND_SECONDS = 0.3;
export function landSquash(t: number): number {
  const keys = LAND_KEYS;
  if (!(t >= 0) || t >= keys[keys.length - 1][0]) return 1;
  for (let i = 1; i < keys.length; i++) {
    const [t1, v1] = keys[i];
    if (t > t1) continue;
    const [t0, v0] = keys[i - 1], u = (t - t0) / (t1 - t0);
    return v0 + (v1 - v0) * u * u * (3 - 2 * u);
  }
  return 1;
}

/** Squash, kick and drop-in, written onto the model group (the root keeps the droop and the cell position). */
export function feel(v: TowerView, dt: number): void {
  const s = v.model.storeys;
  if (v.dropAt < TowerFeel.DropSeconds) {
    v.dropAt += dt;
    v.root.position.y = v.restY + TowerFeel.drop(v.dropAt);
    if (v.dropAt >= TowerFeel.DropSeconds) { v.root.position.y = v.restY; v.landAt = 0; }   // lands with a squash
  }
  if (v.landAt < LAND_SECONDS) {
    v.landAt += dt;
    const sy = landSquash(v.landAt), sxz = 1 / Math.sqrt(sy);
    s.scale.set(sxz, sy, sxz);
    return;
  }
  if (v.shotAt < TowerFeel.SquashSeconds || v.shotAt < TowerFeel.KickSeconds) {
    v.shotAt += dt;
    const sy = TowerFeel.squash(v.shotAt), sxz = 1 / Math.sqrt(sy);
    s.scale.set(sxz, sy, sxz);
    const k = TowerFeel.kick(v.shotAt) * TowerFeel.KickMetres;
    s.position.set(v.kickX * k, 0, v.kickZ * k);
  }
}

/**
 * The clips' pose, then the aim on top. The mixer only writes a node when its blended value changed since the
 * last frame, so where every playing clip holds the weapon still (a `fire` played solo whose weapon keys are
 * constant) the node kept last frame's AIMED pose and the aim was applied again on top of it: the bow spun a yaw
 * further every frame. The weapon goes back to the clips' pose before the mixer runs, so the aim (and a bombed
 * tower's droop) is applied once to the clips' pose whether or not the mixer wrote it.
 */
export function poseTowerWeapon(v: TowerView, dt: number): void { v.weaponPose = poseWeapon(v.model, v.weaponPose, v.yaw, dt, v.orbit, v.tilt); }
/**
 * poseWeapon's work on a bare model: returns the clips' pose to restore next frame (null without a weapon). With
 * `orbit` (the rest pivot's offset from the tower's axis, in the weapon parent's frame) the pivot also swings round
 * the axis with the aim, so the weapon stays on the side of the tower it shoots from.
 */
export function poseWeapon(m: Pick<TowerModel, "weapon" | "weaponRest" | "rig">, pose: WeaponPose | null, yaw: number, dt: number, orbit: THREE.Vector3 | null = null, tilt: THREE.Quaternion | null = null): WeaponPose | null {
  const w = m.weapon;
  if (w && pose) { w.position.copy(pose.position); w.quaternion.copy(pose.quaternion); }
  m.rig.update(dt);
  if (!w) return null;
  const out = pose ?? { position: w.position.clone(), quaternion: w.quaternion.clone() };
  if (!pose) m.rig.overlay = weaponOverlay(w, out);              // a clip starting saves the clips' pose, not the aim
  out.position.copy(w.position); out.quaternion.copy(w.quaternion);
  aimWeapon(m as TowerModel, yaw, tilt);
  if (orbit) w.position.add(_orbit.copy(orbit).applyAxisAngle(UP, yaw)).sub(orbit);
  return out;
}

/** The loaded round through the `fire` clip (TowerFeel.Nocked): shown through the draw, gone from the release. */
export function nock(v: TowerView): void {
  const spec = TowerFeel.Nocked[v.arch], shot = TOWER_SHOT[v.arch];
  if (!spec || !shot) return;
  if (v.nocked === undefined) v.nocked = hqNode(v.model.storeys, v.model.name, spec.part);
  if (!v.nocked) return;
  const k = TowerFeel.nockedScale(v.fireAt * v.fireRate * RELEASE_FPS, shot.release, spec.reload);
  if (k !== null) v.nocked.scale.setScalar(Math.max(HIDDEN_SCALE, k));
}
/** What "hidden" scales a part to: the clips' own 0.001, a scale the bone matrices stay invertible at. */
export const HIDDEN_SCALE = 0.001;
/**
 * Towers whose weapon turns round the tower's axis rather than in place. The fire tower's nozzle is mounted 0.14 m
 * in front of the axis (at the brazier deck's rim): yawed in place, a shot behind the tower swung the nozzle back
 * through the brazier, its mouth ~0.1 m past the axis on the far side, and the muzzle gout came out of the middle
 * of the roof (TOWER_FX_2026-09-24 follow-up). Orbiting, the nozzle stays on the rim facing the target. The poison
 * tower's tier-1 pipe is pivoted 0.29 m off the axis beside the cauldron and swung its nozzle across the cauldron the
 * same way (GUIDE_2026-09-24 follow-up); its tier-2/3 pipes sit on the axis, where the orbit changes nothing.
 */
export const ORBITING_WEAPONS: ReadonlySet<string> = new Set(["fire", "poison"]);
/** The weapon's rest pivot off the tower's axis in its parent's frame (level), for an orbiting weapon; else null. */
export function orbitOf(v: TowerView): THREE.Vector3 | null { return weaponOrbit(v.model, v.arch, v.root); }
/** orbitOf for a bare model whose tower axis passes through `axis`'s origin (the hub's stands). */
export function weaponOrbit(m: Pick<TowerModel, "weapon" | "weaponRest">, arch: string, axis: THREE.Object3D): THREE.Vector3 | null {
  const w = m.weapon, rest = m.weaponRest;
  if (!w?.parent || !rest || !ORBITING_WEAPONS.has(arch)) return null;
  axis.updateWorldMatrix(true, true);
  const at = w.parent.worldToLocal(axis.getWorldPosition(_a));
  return rest.position.clone().sub(at).setY(0);
}

/** Idle life through the pooled VFX (towers.md F9): coil arcs every 2–4 s, embers every 0.4 s; the fortress's in fortressLife.ts. */
export function idle(v: TowerView, dt: number, fx: VFX): void {
  if (v.isBase) { fortressIdle(v, dt, fx); return; }
  if (v.arch !== "lightning" && v.arch !== "fire") return;
  v.idleIn -= dt;
  if (v.idleIn > 0) return;
  if (v.arch === "lightning") {
    v.idleIn = TowerFeel.ArcEvery[0] + Math.random() * (TowerFeel.ArcEvery[1] - TowerFeel.ArcEvery[0]);
    const coils = v.coils ??= modelParts(v, "coil");
    if (coils.length < 2) return;
    const i = Math.floor(Math.random() * coils.length), j = (i + 1 + Math.floor(Math.random() * (coils.length - 1))) % coils.length;
    fx.idleArc(coils[i].getWorldPosition(_a), coils[j].getWorldPosition(_b));
  } else {
    v.idleIn = TowerFeel.EmberEvery * (0.75 + Math.random() * 0.5);
    const flames = v.flames ??= modelParts(v, "flame");
    if (flames.length === 0) return;
    fx.idleEmber(flames[Math.floor(Math.random() * flames.length)].getWorldPosition(_a));
    // now and then a wisp of soot off the main brazier's flame (flame0, the largest), so the fire reads as burning
    if (Math.random() < SOOT_CHANCE) fx.idleSoot(flames[0].getWorldPosition(_a).setY(_a.y + SOOT_LIFT));
  }
}

/** The share of a fire tower's idle embers that also send up a wisp of soot, and how far above the flame's foot (m). */
export const SOOT_CHANCE = 0.35;
export const SOOT_LIFT = 0.3;

/** The model's numbered parts (`coil0`, `coil1` …) under either spelling of the node name (see hqNode). */
export function modelParts(v: TowerView, part: string): THREE.Object3D[] {
  if (!v.model.name) return [];
  const re = new RegExp(`^${v.model.name}\\.?${part}\\d+$`);
  const out: THREE.Object3D[] = [];
  v.model.storeys.traverse((o) => { if (re.test(o.name)) out.push(o); });
  return out;
}

/** The tier-3 crown: three element motes orbiting above the top, a beat every 0.1 s (ambient priority). */
export function crownTick(v: TowerView, dt: number, fx: VFX): void {
  v.crownIn -= dt;
  if (v.crownIn > 0) return;
  v.crownIn = 0.1;
  const top = _a.copy(v.root.position); top.y = v.box.max.y + 0.25;
  fx.crown(top, performance.now() / 1000, towerColour(v.arch));
}

/** Conductor HDR 1 → 4 over the charge (towers.md hero moment; ART_WOW_PASS "The Fortress Strike"); then ready. */
export function chargeGlow(v: TowerView, dt: number, glowFlash: GlowFlash | null): void {
  v.chargeAt += dt;
  const k = Math.min(1, v.chargeAt / v.chargeLen);
  glowFlash?.(v.model.storeys, 1 + (FortressRhythm.ChargedGlow - 1) * k * k, FortressRhythm.GlowHoldSeconds);
  if (k >= 1) { v.phase = "ready"; v.chargeAt = Infinity; }
}

/**
 * The Dragonlord reveal (ART_WOW_PASS Phase 2 §6): the skin's `show` clip uncoils the dragon from the keep; the
 * ground heaves as it starts, and at `BreathAt` it exhales a fire gout from its jaw down the road. The Moonstone
 * Keep's `show` raises its moon instead, and that beat is a moonrise flare (its kit's `reveal`).
 */
export const BREATH_AT = 2.0;
export function reveal(v: TowerView, dt: number, fx: VFX | null, baseHeading: Heading): void {
  const before = v.revealAt;
  v.revealAt += dt;
  if (!fx) return;
  if (before < 0.15 && v.revealAt >= 0.15) fx.bossArrival(keepCentre(v, _a), { size: 0.9 });  // the keep heaves
  if (!v.breathed && v.revealAt >= BREATH_AT) {
    v.breathed = true;
    const beat = SkinFx.fortressKit(v.model.name)?.reveal;
    if (beat === "moonrise") { moonrise(v, fx); return; }
    if (beat === "starburst") { starburst(v, fx); return; }
    if (beat === "drake") { fortressAwaken(v, fx); v.companion?.reveal(fx); return; }   // the Dragonlord's drake swoops in
    const jaw = hqNode(v.model.storeys, v.model.name, "jaw");
    if (!jaw) { fortressAwaken(v, fx); return; }                                // the stock fortress: no dragon to breathe
    const from = jaw.getWorldPosition(_a);
    const h = baseHeading;
    const dir = _b.set([0, 1, 0, -1][h], -0.35, [1, 0, -1, 0][h]);            // down the gate's heading
    fx.dragonBreath(from, dir);
  }
  if (v.revealAt > 3.5) v.revealAt = Infinity;
}

/** The Moonstone Keep's reveal beat: the risen moon flares cold white, a silver ring spreads over the keep. */
const MOONRISE_RIM: [number, number, number] = [0.62, 0.8, 1];
function moonrise(v: TowerView, fx: VFX): void {
  const moon = hqNode(v.model.storeys, v.model.name, "condgem0");
  const at = moon ? moon.getWorldPosition(_a) : _a.copy(v.root.position).setY(v.box.max.y);
  fx.flash(at, 1.4, [0.85, 0.92, 1]);
  fx.glowPulse(at, { size: 1.6, life: 0.7, color: MOONRISE_RIM });
  fx.ring(keepCentre(v, _b), { size: 3.2, life: 0.8, color: MOONRISE_RIM, flat: true, bright: 1.6 });
  fx.sparkles(at, 8, { color: MOONRISE_RIM, radius: 0.5, rise: 0.4, life: 1.2, size: 0.1 });
}

/**
 * The Starfall Citadel's reveal beat: the star has just landed on the keep (its `show` drops it from the sky) — a
 * white-rose flash, a lilac bloom, an upright halo and a flat ring spreading over the keep, rose sparkles and cyan
 * glints thrown off the star, and a second, smaller ring a beat later.
 */
const STARBURST_ROSE: [number, number, number] = [1, 0.62, 0.9];
const STARBURST_LILAC: [number, number, number] = [0.78, 0.6, 1];
const STARBURST_CYAN: [number, number, number] = [0.6, 0.92, 1];
function starburst(v: TowerView, fx: VFX): void {
  const star = hqNode(v.model.storeys, v.model.name, "condgem0");
  const at = star ? star.getWorldPosition(_a).clone() : _a.copy(v.root.position).setY(v.box.max.y).clone();
  const ground = keepCentre(v, _b).clone();
  fx.flash(at, 1.6, [1, 0.9, 1]);
  fx.glowPulse(at, { size: 1.8, life: 0.8, color: STARBURST_LILAC });
  fx.ring(at, { size: 1.4, life: 0.6, color: STARBURST_ROSE, flat: false, bright: 1.8 });
  fx.ring(ground, { size: 3.4, life: 0.9, color: STARBURST_LILAC, flat: true, bright: 1.6 });
  fx.sparkles(at, 8, { color: STARBURST_ROSE, radius: 0.6, rise: 0.5, life: 1.3, size: 0.12 });
  fx.sparkles(at, 6, { color: STARBURST_CYAN, radius: 0.9, rise: 0.2, life: 1.0, size: 0.09 });
  fx.later(0.25, () => fx.ring(ground, { size: 2.2, life: 0.7, color: STARBURST_ROSE, flat: true, bright: 1.4 }));
}

/** Ground point under the middle of the tower's rest box (the fortress's keep sits behind its gate cell). */
export function keepCentre(v: TowerView, out: THREE.Vector3): THREE.Vector3 {
  v.box.getCenter(out);
  out.y = groundAt(out.x, out.z);
  return out;
}

/** The element colour of a tower (VFX.TOWER_COLOR), gold when unknown. */
export function towerColour(arch: string): number[] { return (VFX.TOWER_COLOR[arch] as number[] | undefined) ?? [1, 0.85, 0.35]; }

/**
 * A fire tower's braziers (vfx.js FIRE, `towerFlame`): each flame cone is drawn as a flickering sprite flame on the
 * hot ramp, and the cone — one flat pale-orange emissive colour — is hidden. The clips scale the cones (the idle
 * flicker, the `fire` flare, the build), and the sprite follows that scale. The mixer writes a node only when its
 * value changes, so the clips' last scale is kept here and the hidden scale put back after every rig update; with no
 * VFX (reduced effects, or before the atlas loads) the cones stand as modelled.
 */
export function braziers(v: TowerView, fx: VFX | null, clock: number): void {
  for (const b of v.braziers!) {
    const n = b.node;
    if (n.scale.x !== HIDDEN_SCALE) b.clip.copy(n.scale);          // the mixer wrote it this frame
    if (!fx) { n.scale.copy(b.clip); continue; }
    n.scale.setScalar(HIDDEN_SCALE);
    n.parent!.updateWorldMatrix(true, false);
    // the `fire` clip's flare (the brazier roars before the lob) is kept to BrazierFlare × the rest height: at the
    // clip's full swell the sprite flame stood over the whole roof, a white column
    const cap = BRAZIER_FLARE * b.rest;
    const s = b.clip.y > cap ? cap / b.clip.y : 1;
    const m = _m.compose(n.position, n.quaternion, _b.copy(b.clip).multiplyScalar(s)).premultiply(n.parent!.matrixWorld);
    const base = _a.copy(b.base).applyMatrix4(m), tip = _b.copy(b.tip).applyMatrix4(m);
    fx.towerFlame(base, tip.y - base.y, clock, b.seed);
  }
}

/**
 * The cones a fire tower's flames stand in for (braziers); null for other towers. Each cone's base and tip are
 * measured once per model, on the first fresh clone at rest, in its node's own frame (the clones share it).
 */
export function measureBraziers(v: TowerView): Brazier[] | null {
  if (v.arch !== "fire" && !v.isBase) return null;     // the fortress's torches and braziers burn as sprites too
  const nodes = modelParts(v, "flame");
  let cones = CONE_CACHE.get(v.model.name);
  if (!cones) {
    const root = v.model.storeys;
    root.updateMatrixWorld(true);
    cones = [];
    for (const node of nodes) {
      const box = partBox(root, node);
      if (box.isEmpty()) continue;
      const toNode = _m.copy(node.matrixWorld).invert().multiply(root.matrixWorld);
      const cx = (box.min.x + box.max.x) / 2, cz = (box.min.z + box.max.z) / 2;
      cones.push({ name: node.name, base: new THREE.Vector3(cx, box.min.y, cz).applyMatrix4(toNode), tip: new THREE.Vector3(cx, box.max.y, cz).applyMatrix4(toNode) });
    }
    CONE_CACHE.set(v.model.name, cones);
  }
  const out: Brazier[] = [];
  for (const c of cones) {
    const node = nodes.find((n) => n.name === c.name);
    if (node) out.push({ node, clip: node.scale.clone(), rest: node.scale.y, base: c.base, tip: c.tip, seed: Math.random() });
  }
  return out.length ? out : null;
}
/** The tallest a brazier's sprite flame grows under the clips, over its height at rest. */
export const BRAZIER_FLARE = 1.5;
/** Per fire-tower model: its flame cones' base and tip in their nodes' frames (measureBraziers). */
const CONE_CACHE = new Map<string, { name: string; base: THREE.Vector3; tip: THREE.Vector3 }[]>();
